import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Zap, Search, Rocket, Star, Download, Eye, ChevronRight, Sparkles, X, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { appTemplates, categories } from '../templates';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const GRADIENTS = {
  blue: 'from-blue-600/30 via-blue-800/20 to-slate-900',
  green: 'from-emerald-600/30 via-emerald-800/20 to-slate-900',
  purple: 'from-purple-600/30 via-purple-800/20 to-slate-900',
  indigo: 'from-indigo-600/30 via-indigo-800/20 to-slate-900',
  yellow: 'from-amber-600/30 via-amber-800/20 to-slate-900',
  red: 'from-red-600/30 via-red-800/20 to-slate-900',
  teal: 'from-teal-600/30 via-teal-800/20 to-slate-900',
  orange: 'from-orange-600/30 via-orange-800/20 to-slate-900',
  pink: 'from-pink-600/30 via-pink-800/20 to-slate-900',
  cyan: 'from-cyan-600/30 via-cyan-800/20 to-slate-900',
  emerald: 'from-emerald-600/30 via-emerald-800/20 to-slate-900',
  violet: 'from-violet-600/30 via-violet-800/20 to-slate-900',
};

const POPULARITY = {
  chatbot: { stars: 4.9, uses: '12.4K' },
  'content-generator': { stars: 4.7, uses: '8.2K' },
  'image-generator': { stars: 4.8, uses: '15.1K' },
  'code-assistant': { stars: 4.9, uses: '22.3K' },
  'data-analyzer': { stars: 4.5, uses: '6.8K' },
  'voice-assistant': { stars: 4.3, uses: '3.1K' },
  'email-assistant': { stars: 4.6, uses: '9.7K' },
  'recipe-finder': { stars: 4.4, uses: '5.2K' },
  'social-media-manager': { stars: 4.7, uses: '11.5K' },
  'translation-tool': { stars: 4.8, uses: '7.9K' },
  'tutor-bot': { stars: 4.6, uses: '8.4K' },
  'resume-builder': { stars: 4.5, uses: '6.3K' },
};

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [deploying, setDeploying] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [livePreviewId, setLivePreviewId] = useState(null);

  const filteredTemplates = appTemplates.filter(t => {
    const matchCat = selectedCategory === 'All' || t.category === selectedCategory;
    const matchSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        t.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleUseTemplate = (template) => {
    toast({ title: 'Template Selected!', description: `Creating ${template.name}...` });
    api.post('/templates/track', { template_id: template.id, template_name: template.name }).catch(() => {});
    navigate('/new-project', {
      state: { template: { name: template.name, description: template.description, style: 'modern', stack: template.stack, aiModel: template.aiModel } }
    });
  };

  const handleQuickDeploy = async (template) => {
    setDeploying(template.id);
    try {
      const res = await api.post('/deploy/vercel/quick', {
        project_id: `tpl-${template.id}`,
        project_name: template.name.replace(/\s+/g, '-').toLowerCase()
      });
      if (res.data.success) {
        toast({ title: 'Deployed!', description: `${template.name} is live at ${res.data.url}` });
      } else {
        toast({ title: 'Deploy Failed', description: res.data.message, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Deployment failed. Check your Vercel token.', variant: 'destructive' });
    } finally { setDeploying(null); }
  };

  const complexityColor = (c) => ({
    Easy: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    Medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    Hard: 'bg-red-500/20 text-red-400 border-red-500/30',
  }[c] || 'bg-slate-500/20 text-muted-foreground');

  const modelLabel = (m) => ({
    claude: 'Claude', openai: 'GPT-5.2', grok: 'Grok', auto: 'Auto'
  }[m] || m);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-violet-600/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 py-8 relative">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4 text-muted-foreground hover:text-foreground hover:bg-accent" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-end justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white" data-testid="marketplace-title">Template Marketplace</h1>
              </div>
              <p className="text-muted-foreground max-w-lg">Browse, preview, and deploy pre-built AI applications. Get a production-ready app running in seconds.</p>
            </div>
            <div className="hidden lg:flex items-center gap-6 text-sm">
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">{appTemplates.length}</p>
                <p className="text-muted-foreground">Templates</p>
              </div>
              <div className="w-px h-10 bg-[#282828]" />
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">120K+</p>
                <p className="text-muted-foreground">Deployments</p>
              </div>
              <div className="w-px h-10 bg-[#282828]" />
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">4.7</p>
                <p className="text-muted-foreground">Avg Rating</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="max-w-7xl mx-auto px-6 py-5">
        <div className="flex gap-4 mb-5">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <Input placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 bg-card border-input text-white placeholder:text-muted-foreground h-11" data-testid="template-search" />
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-none">
          {categories.map(cat => (
            <Button key={cat} onClick={() => setSelectedCategory(cat)}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              size="sm"
              className={`whitespace-nowrap ${selectedCategory === cat
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'border-input text-muted-foreground hover:bg-secondary hover:text-foreground'}`}
              data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
              {cat}
            </Button>
          ))}
        </div>

        {/* Template Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredTemplates.map(t => {
            const pop = POPULARITY[t.id] || { stars: 4.5, uses: '1K' };
            const grad = GRADIENTS[t.color] || GRADIENTS.blue;
            const isPreview = previewId === t.id;
            return (
              <Card key={t.id} className="bg-card border-border hover:border-blue-500/30 transition-all group overflow-hidden" data-testid={`template-card-${t.id}`}>
                {/* Preview Banner */}
                <div className={`relative h-36 bg-gradient-to-br ${grad} flex items-center justify-center cursor-pointer`}
                  onClick={() => setPreviewId(isPreview ? null : t.id)}>
                  <span className="text-6xl transform group-hover:scale-110 transition-transform">{t.icon}</span>
                  <div className="absolute top-3 left-3">
                    <Badge className={complexityColor(t.complexity)} data-testid={`complexity-${t.id}`}>{t.complexity}</Badge>
                  </div>
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                    <span className="text-xs text-foreground font-medium">{pop.stars}</span>
                  </div>
                  <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm">
                    <Download className="w-3 h-3 text-foreground/80" />
                    <span className="text-xs text-foreground/80">{pop.uses}</span>
                  </div>
                </div>

                <CardContent className="p-4 space-y-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-foreground font-semibold group-hover:text-blue-400 transition-colors">{t.name}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">{t.category}</p>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{t.description}</p>

                  {/* Meta info */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{t.estimatedTime}</span>
                    <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5" />{modelLabel(t.aiModel)}</span>
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-1">
                    {t.features.slice(0, 3).map((f, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-secondary text-muted-foreground border border-input">{f}</span>
                    ))}
                    {t.features.length > 3 && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-secondary text-muted-foreground">+{t.features.length - 3}</span>
                    )}
                  </div>

                  {/* Preview Expand */}
                  {isPreview && (
                    <div className="p-3 bg-muted rounded-lg border border-border space-y-2">
                      <p className="text-xs text-muted-foreground font-medium">All Features:</p>
                      {t.features.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <ChevronRight className="w-3 h-3 text-blue-400" />{f}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-1">
                    <Button onClick={() => handleUseTemplate(t)} className="flex-1 bg-blue-600 hover:bg-blue-500 h-9 text-sm gap-1.5"
                      data-testid={`use-template-${t.id}`}>
                      <Sparkles className="w-3.5 h-3.5" /> Use Template
                    </Button>
                    <Button onClick={() => handleQuickDeploy(t)} disabled={deploying === t.id}
                      variant="outline" className="h-9 px-3 border-violet-500/30 text-violet-400 hover:bg-violet-500/10"
                      data-testid={`deploy-template-${t.id}`}>
                      {deploying === t.id ? <span className="w-4 h-4 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                    </Button>
                    <Button onClick={() => setPreviewId(isPreview ? null : t.id)}
                      variant="outline" className="h-9 px-3 border-input text-muted-foreground hover:bg-accent"
                      data-testid={`preview-template-${t.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button onClick={() => setLivePreviewId(t.id)}
                      variant="outline" className="h-9 px-3 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                      data-testid={`live-preview-${t.id}`}>
                      <Monitor className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-muted-foreground/70 mx-auto mb-4" />
            <p className="text-muted-foreground text-lg mb-2">No templates found</p>
            <Button onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
              variant="outline" className="border-input text-foreground/80 hover:bg-secondary">
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Live Preview Modal */}
      <Dialog open={!!livePreviewId} onOpenChange={() => setLivePreviewId(null)}>
        <DialogContent className="max-w-4xl h-[80vh] bg-background border-border p-0 overflow-hidden" aria-describedby="live-preview-desc">
          <span id="live-preview-desc" className="sr-only">Live preview of the selected template</span>
          {livePreviewId && (() => {
            const t = appTemplates.find(tp => tp.id === livePreviewId);
            if (!t) return null;
            return (
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-border bg-card">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{t.icon}</span>
                    <div>
                      <h3 className="text-foreground font-semibold">{t.name}</h3>
                      <p className="text-xs text-muted-foreground">{t.category} &middot; {t.estimatedTime}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => { setLivePreviewId(null); handleUseTemplate(t); }} className="bg-blue-600 hover:bg-blue-500 gap-1.5 h-8 text-sm" data-testid="preview-use-template-btn">
                      <Sparkles className="w-3.5 h-3.5" /> Use Template
                    </Button>
                    <Button onClick={() => setLivePreviewId(null)} variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-hidden" data-testid="live-preview-frame">
                  <TemplatePreviewRenderer template={t} />
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TemplatePreviewRenderer = ({ template }) => {
  const PREVIEW_UI = {
    chatbot: (
      <div className="h-full flex flex-col bg-background">
        <div className="p-4 border-b border-border bg-card">
          <div className="flex items-center gap-2"><span className="text-lg">💬</span><span className="text-foreground font-semibold text-sm">AI Chatbot</span><span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full ml-auto">Online</span></div>
        </div>
        <div className="flex-1 p-4 space-y-3 overflow-y-auto">
          <div className="flex gap-3"><div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">AI</div><div className="bg-secondary rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[70%]"><p className="text-sm text-foreground/80">Hello! I'm your AI assistant. How can I help you today?</p></div></div>
          <div className="flex gap-3 justify-end"><div className="bg-blue-600 rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[70%]"><p className="text-sm text-foreground">Tell me about your capabilities</p></div></div>
          <div className="flex gap-3"><div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">AI</div><div className="bg-secondary rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[70%]"><p className="text-sm text-foreground/80">I can help with real-time conversations, answer questions, generate content, and maintain context across our chat. I support custom personality settings too!</p></div></div>
        </div>
        <div className="p-4 border-t border-border bg-card"><div className="flex gap-2"><div className="flex-1 h-10 rounded-xl bg-muted border border-input px-3 flex items-center text-muted-foreground text-sm">Type a message...</div><div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center"><span className="text-white text-sm">→</span></div></div></div>
      </div>
    ),
    'code-assistant': (
      <div className="h-full flex bg-background">
        <div className="w-48 bg-card border-r border-border p-3"><p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Files</p><div className="space-y-1">{['App.jsx','index.css','utils.js','api.js'].map(f=><div key={f} className="px-2 py-1 text-xs text-muted-foreground rounded hover:bg-secondary">{f}</div>)}</div></div>
        <div className="flex-1 flex flex-col"><div className="p-3 border-b border-border bg-card"><span className="text-white text-sm font-semibold">Code Assistant</span></div><div className="flex-1 p-4 font-mono text-xs space-y-1 text-muted-foreground bg-background"><p><span className="text-violet-400">const</span> <span className="text-blue-300">App</span> = () {'{'}</p><p className="pl-4"><span className="text-violet-400">return</span> (</p><p className="pl-8">&lt;<span className="text-emerald-400">div</span> <span className="text-amber-400">className</span>=<span className="text-orange-300">"app"</span>&gt;</p><p className="pl-12">&lt;<span className="text-emerald-400">h1</span>&gt;Hello World&lt;/<span className="text-emerald-400">h1</span>&gt;</p><p className="pl-8">&lt;/<span className="text-emerald-400">div</span>&gt;</p><p className="pl-4">)</p><p>{'}'};</p></div></div>
      </div>
    ),
    'image-generator': (
      <div className="h-full flex flex-col bg-background">
        <div className="p-4 border-b border-border bg-card"><span className="text-foreground font-semibold text-sm">AI Image Studio</span></div>
        <div className="flex-1 p-6"><div className="grid grid-cols-2 gap-4"><div className="aspect-square rounded-xl bg-gradient-to-br from-purple-600/30 via-blue-600/20 to-pink-600/30 border border-input flex items-center justify-center"><span className="text-4xl">🎨</span></div><div className="aspect-square rounded-xl bg-gradient-to-br from-emerald-600/30 via-teal-600/20 to-cyan-600/30 border border-input flex items-center justify-center"><span className="text-4xl">🖼️</span></div><div className="aspect-square rounded-xl bg-gradient-to-br from-amber-600/30 via-orange-600/20 to-red-600/30 border border-input flex items-center justify-center"><span className="text-4xl">🌄</span></div><div className="aspect-square rounded-xl bg-gradient-to-br from-indigo-600/30 via-violet-600/20 to-fuchsia-600/30 border border-input flex items-center justify-center"><span className="text-4xl">✨</span></div></div></div>
        <div className="p-4 border-t border-border bg-card"><div className="h-10 rounded-xl bg-muted border border-input px-3 flex items-center text-muted-foreground text-sm">Describe the image you want to generate...</div></div>
      </div>
    ),
  };

  const defaultPreview = (
    <div className="h-full flex flex-col bg-background">
      <div className="p-4 border-b border-border bg-card flex items-center gap-2">
        <span className="text-lg">{template.icon}</span><span className="text-foreground font-semibold text-sm">{template.name}</span>
      </div>
      <div className="flex-1 p-6 flex flex-col items-center justify-center text-center">
        <span className="text-5xl mb-4">{template.icon}</span>
        <h3 className="text-xl font-bold text-foreground mb-2">{template.name}</h3>
        <p className="text-muted-foreground text-sm max-w-md mb-4">{template.description}</p>
        <div className="flex flex-wrap gap-2 justify-center">{template.features.map((f,i)=><span key={i} className="px-3 py-1 text-xs rounded-full bg-secondary text-muted-foreground border border-input">{f}</span>)}</div>
      </div>
      <div className="p-4 border-t border-border bg-card"><div className="flex items-center gap-2 text-xs text-muted-foreground"><span>Stack: {template.stack}</span><span>&middot;</span><span>Model: {template.aiModel}</span><span>&middot;</span><span>{template.estimatedTime}</span></div></div>
    </div>
  );

  return PREVIEW_UI[template.id] || defaultPreview;
};

export default TemplatesPage;
